import { useCallback, useState } from 'react'
import { Box, Paper, Typography, TypographyProps } from '@mui/material'
import DocumentDetailDisplay from './DocumentDetailDisplay'
import PublishIcon from '@mui/icons-material/Publish'
import { useSnackbar } from '@/utilities/hooks'
import InfoDialog from './InfoDiaglog'

function DragDropFileUpload({
  fileType,
  onFileUpload,
  uploadTips,
  file,
  status,
  id,
  maxFileSizeMB,
  maxFileLimitWarning,
  maxFileLimitWarningType
}: {
  fileType: string
  onFileUpload: (file: File) => void
  file: File | null
  uploadTips?: string
  status?: string
  id?: string
  maxFileSizeMB?: number
  maxFileLimitWarning?: string
  maxFileLimitWarningType?: 'alert' | 'dialog'
}) {
  const [dragOver, setDragOver] = useState(false)
  const { showAlertSnackbar } = useSnackbar()

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setDragOver(true)
    },
    []
  )

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setDragOver(false)
    },
    []
  )

  const showValidationError = useCallback(
    (message: string) => {
      if (maxFileLimitWarningType === 'dialog') {
        InfoDialog.open({
          title: 'File Upload Error',
          message,
          type: 'error'
        })
      } else {
        showAlertSnackbar({ message, type: 'error' })
      }
    },
    [maxFileLimitWarningType, showAlertSnackbar]
  )

  const validateFile = useCallback(
    (file: File) => {
      if (!file) return false

      // Validate file type by extension
      if (fileType) {
        const allowedExtensions = fileType
          .split(',')
          .map(ext => ext.trim().toLowerCase())
        const fileName = file.name.toLowerCase()
        const fileExtension = fileName.slice(fileName.lastIndexOf('.'))
        if (!allowedExtensions.includes(fileExtension)) {
          showValidationError(
            `Invalid file type. Accepted formats: ${allowedExtensions.join(', ')}`
          )
          return false
        }
      }

      // Validate file size
      if (maxFileSizeMB && file.size > maxFileSizeMB * 1024 * 1024) {
        showValidationError(
          maxFileLimitWarning ||
            `The file size exceeds the maximum limit of ${maxFileSizeMB}MB. Please upload a smaller file.`
        )
        return false
      }

      return true
    },
    [fileType, maxFileSizeMB, maxFileLimitWarning, showValidationError]
  )

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setDragOver(false)
      if (event.dataTransfer.files && event.dataTransfer.files[0]) {
        if (validateFile(event.dataTransfer.files[0])) {
          onFileUpload(event.dataTransfer.files[0])
        }
      }
    },
    [onFileUpload, validateFile]
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files[0]) {
        if (validateFile(event.target.files[0])) {
          onFileUpload(event.target.files[0])
        }
        event.target.value = ''
      }
    },
    [onFileUpload, validateFile]
  )

  return (
    <div>
      <input
        accept={fileType}
        style={{ display: 'none' }}
        id={id || 'upload_file'}
        type='file'
        onChange={handleChange}
      />

      {file ? (
        <label htmlFor={id || 'upload_file'}>
          <DocumentDetailDisplay file={file} status={status || 'Complete'} />
        </label>
      ) : (
        <Paper
          variant='outlined'
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: dragOver ? '1.5px dashed #000' : '1.5px dashed #002678',
            padding: 20,
            textAlign: 'center',
            cursor: 'pointer',
            background: dragOver
              ? 'rgba(49, 111, 245, 0.2)'
              : 'rgba(49, 111, 245, 0.08)'
          }}>
          <div>
            <Box display='flex' flexDirection='column' alignItems='center'>
              <PublishIcon color='primary' style={{ fontSize: 30 }} />
            </Box>
            <div style={{ paddingTop: 20 }}>
              <label
                style={{
                  color: '#002678',
                  textDecoration: 'underline',
                  fontWeight: '600'
                }}
                htmlFor={id || 'upload_file'}>
                {' '}
                <span>Click to upload</span>
              </label>
              <span> or drag and drop</span>
            </div>

            <div style={{ paddingTop: 10, color: 'rgba(0, 0, 0, 0.6)' }}>
              <Typography
                variant={'body5' as TypographyProps['variant']}
                fontSize={'13px'}>
                {uploadTips ?? fileType}
              </Typography>
            </div>
          </div>
        </Paper>
      )}
    </div>
  )
}

export default DragDropFileUpload
